/**
 * use-campaign-enrollments.ts — Enrollments batch avec infos contact
 *
 * Charge tous les enrollments d'une equipe avec les donnees contact jointes,
 * groupes par sequence_id pour affichage dans l'onglet Campagnes.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTeamId } from "@/hooks/use-team-id";
import { useToast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";

type EnrollmentRow = Database["public"]["Tables"]["sequence_enrollments"]["Row"];
type EnrollmentStatus = EnrollmentRow["status"];

export interface EnrichedEnrollment {
  id: string;
  sequenceId: string;
  contactId: string | null;
  currentStep: number;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  nextRunAt: string | null;
  contact: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    name: string | null;
    email: string | null;
    stage: string | null;
    lifecyclePhase: string | null;
  } | null;
}

export function useCampaignEnrollments() {
  const { teamId } = useTeamId();

  return useQuery({
    queryKey: ["campaign-enrollments-batch", teamId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sequence_enrollments")
        .select(
          `id, sequence_id, contact_id, current_step, status, started_at, completed_at, next_run_at,
           immo_contacts(id, first_name, last_name, name, email, stage, lifecycle_phase)`
        )
        .eq("team_id", teamId!)
        .order("created_at", { ascending: false })
        .limit(500);

      if (error) throw error;

      const bySequence = new Map<string, EnrichedEnrollment[]>();

      for (const row of data ?? []) {
        const contact = row.immo_contacts as unknown as {
          id: string;
          first_name: string | null;
          last_name: string | null;
          name: string | null;
          email: string | null;
          stage: string | null;
          lifecycle_phase: string | null;
        } | null;

        const enriched: EnrichedEnrollment = {
          id: row.id,
          sequenceId: row.sequence_id,
          contactId: row.contact_id,
          currentStep: (row.current_step as number) ?? 0,
          status: (row.status as string) ?? "active",
          startedAt: row.started_at,
          completedAt: row.completed_at,
          nextRunAt: row.next_run_at,
          contact: contact
            ? {
                id: contact.id,
                firstName: contact.first_name,
                lastName: contact.last_name,
                name: contact.name,
                email: contact.email,
                stage: contact.stage,
                lifecyclePhase: contact.lifecycle_phase,
              }
            : null,
        };

        const list = bySequence.get(row.sequence_id) ?? [];
        list.push(enriched);
        bySequence.set(row.sequence_id, list);
      }

      return bySequence;
    },
    enabled: !!teamId,
    staleTime: 2 * 60 * 1000,
  });
}

export function invalidateEnrollmentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  teamId: string | null
) {
  queryClient.invalidateQueries({ queryKey: ["campaign-enrollments-batch", teamId] });
  queryClient.invalidateQueries({ queryKey: ["sequence-stats", teamId] });
  queryClient.invalidateQueries({ queryKey: ["contact-active-enrollments", teamId] });
  queryClient.invalidateQueries({ queryKey: ["sequence-enrollments"] });
  queryClient.invalidateQueries({ queryKey: ["sequence-enrollments-detail"] });
}

export function useUpdateCampaignEnrollmentStatus() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      enrollmentId,
      status,
    }: {
      enrollmentId: string;
      status: EnrollmentStatus;
    }) => {
      const { error } = await supabase
        .from("sequence_enrollments")
        .update({ status })
        .eq("id", enrollmentId)
        .eq("team_id", teamId!);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      invalidateEnrollmentQueries(queryClient, teamId);
      toast({
        title: variables.status === "paused" ? "Contact mis en pause" : "Contact réactivé",
      });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de modifier le statut du contact dans la campagne.",
        variant: "destructive",
      });
    },
  });
}

export function useRemoveCampaignEnrollment() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (enrollmentId: string) => {
      const { error } = await supabase
        .from("sequence_enrollments")
        .delete()
        .eq("id", enrollmentId)
        .eq("team_id", teamId!);

      if (error) throw error;
    },
    onSuccess: () => {
      invalidateEnrollmentQueries(queryClient, teamId);
      toast({ title: "Contact retiré de la campagne" });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de retirer ce contact de la campagne.",
        variant: "destructive",
      });
    },
  });
}
